<template>
  <div class="organization-locations mt-5" v-if="organization">

    <v-row>
      <v-col class="text-right">
        <v-btn v-if="canWrite" small color="primary" @click="$refs.locationModal.open()">
          <v-icon small left>mdi-plus</v-icon>
          {{ $t('location.add') }}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="Location" :organization-id="$route.params.id" />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <search-field
            :placeholder="$t('search')"
            :search.sync="search"
            :options.sync="options"
            clearable
        />
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-10">
      <v-col>
        <v-data-table
            class="location-list"
            :headers="headers"
            :items="items"
            :loading="loading"
            :options.sync="options"
            :server-items-length="total"
            :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
            @click:row="row => $router.push({name: 'location.detail', params: {id: row.id}})"
        >
          <template #item.name="{item}">
            <router-link :to="{name: 'location.detail', params: {id: item.id}}">
              {{ item.name }}
            </router-link>
          </template>

          <template #item.accessibility="{value}">
            {{ value ? $t('enums.location-accessibility.' + value) : '-' }}
          </template>

          <template #item.organization.name="{item}">
            <template v-if="item.organization">{{ item.organization.name }}</template>
            <template v-else>-</template>
          </template>

          <template #item.address="{value}">
            <template v-if="value">
              {{ value.street }} {{ value.number }}, {{ value.zipcode }} {{ value.city }} - {{ value.country }}
            </template>
            <template v-else>-</template>
          </template>

          <template #item.count="{item, value}">
            <div class="counters" v-if="value > 0">
              <div class="statues">
                <template v-for="(count, status) in item.countByType">
                  <v-tooltip bottom :disabled="!status" :key="status">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="counter" :class="{[status.toLowerCase()]: true}">
                        <v-icon v-if="status" left>ico-status-{{ status.toLowerCase() }}</v-icon> {{ count }}
                      </div>
                    </template>
                    {{ $t('enums.evse-status.' + status) }}
                  </v-tooltip>
                </template>
              </div>
            </div>
            <div v-else>-</div>
          </template>

        </v-data-table>
      </v-col>
    </v-row>

    <!-- Same modal as the global locations list; the organisation is filled in for you. -->
    <location-modal
        ref="locationModal"
        create-only
        address-only
        :default-organization-id="organization.id"
        @location-created="onLocationCreated"
    />

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import LocationRepository from "@repository/LocationRepository";
import LocationModal from "@pages/ChargingPoint/Detail/modals/LocationModal";

export default {

  components: {
    AuditTrailButton,
    LocationModal,
  },

  data() {
    return {
      search: '',
      items: [],
      loading: false,
      options: null,
      total: 0,
    }
  },

  watch: {
    search() {
      this.loadData()
    },

    options() {
      this.loadData()
    },

    'organization.id'() {
      this.loadData()
    },
  },

  computed: {
    organization() {
      return this.$parent.organization ?? null
    },

    canWrite() {
      return this.currentUser.isZE && this.currentUser.permissions.includes('STATION_WRITE')
    },

    headers() {
      return [
        { text: this.$t('name'), value: 'name' },
        { text: this.$t('accessibility'), value: 'accessibility' },
        { text: this.$t('organization.organization'), value: 'organization.name' },
        { text: this.$tc('location.location'), value: 'address' },
        { text: this.$t('location.stations-count'), value: 'count' },
      ]
    },
  },

  methods: {

    // Created from the organisation tab, so we stay in it: the new location appears in the
    // list, one click away, instead of throwing the user onto another page.
    onLocationCreated() {
      this.notifySuccess('location.added')
      this.loadData()
    },

    loadData() {
      if (!this.organization?.id || !this.options) return

      this.loading = true

      let data = {
        all: true,
        search: this.search,
        organizationId: this.organization.id,
      }

      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage

      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      LocationRepository.search(data).then(result => {
        this.total = result.total
        this.items = result.items
        this.loading = false
      }).catch(e => {
        this.notifyError(e)
        this.loading = false
      })
    },

  }
}
</script>

<style lang="scss" scoped>
.counters {
  margin: 5px;
  display: flex;
  height: 50px;
  & > div {
    display: flex;
    flex-grow: 1;
    justify-content: center;
  }
  .counter {
    display: flex;
    flex-grow: 1;
    border-radius: 4px;
    font-size: 14px;
    margin: 3px;
    max-width: 40px;
    padding: 0 25px;
    justify-content: center;
    align-items: center;
    background: #424242;
    color: white;
    box-shadow: 0 0 15px 0 #ccc;
    .v-icon {
      color: white;
      font-size: 16px;
    }
    &.available {
      background: #00897E;
      box-shadow: 0 0 10px 0 #00897E90;
    }
    &.blocked {
      background: #b3028d;
      box-shadow: 0 0 10px 0 #b3028d90;
    }
    &.charging {
      background: #DEAC3C;
      box-shadow: 0 0 10px 0 #DEAC3C90;
    }
    &.offline {
      background: #E53C22;
      box-shadow: 0 0 10px 0 #E53C2290;
    }
    &.outoforder {
      background: #758093;
      box-shadow: 0 0 10px 0 #75809390;
    }
    &.unknown {
      background: #000000;
      box-shadow: 0 0 10px 0 #00000090;
    }
    &.planned {
      background: #1586e3;
      box-shadow: 0 0 10px 0 #1586e390;
    }
  }
  .total {
    display: flex;
    flex-grow: 0;
    width: 40px;
    .counter {
      color: black;
      box-shadow: unset;
    }
  }
}
</style>